import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { AuthModal } from "@/components/AuthModal";
import { CreditPurchaseModal } from "@/components/CreditPurchaseModal";
import { VideoGallery } from "@/components/VideoGallery";
import { ImageGallery } from "@/components/ImageGallery";
import { MusicGallery } from "@/components/MusicGallery";
import { SpeechGallery } from "@/components/SpeechGallery";
import { ChatInterface } from "@/components/ChatInterface";
import { VideoQuickSettings } from "@/components/VideoQuickSettings";
import { ImageQuickSettings } from "@/components/ImageQuickSettings";
import { MusicQuickSettings, type MusicSettings } from "@/components/MusicQuickSettings";
import { SpeechQuickSettings, type SpeechSettings } from "@/components/SpeechQuickSettings";
import { TabNavigation } from "@/components/TabNavigation";
import { LanguageSelector } from "@/components/LanguageSelector";
import { HamburgerMenu } from "@/components/HamburgerMenu";
import { NotificationBell } from "@/components/NotificationBell";
import { OnboardingNotification } from "@/components/OnboardingNotification";
import { PrivacyBanner } from "@/components/PrivacyBanner";
import { UserCreditsStatus } from "@/components/UserCreditsStatus";
import { SubscriptionGate } from "@/components/SubscriptionGate";
import { SubscriptionModal } from "@/components/SubscriptionModal";
import { QueueStatus } from "@/components/QueueStatus";
import { useSubscription } from "@/hooks/useSubscription";
import { useGenerationQueue } from "@/hooks/useGenerationQueue";
import { useTheme } from "next-themes";
import { Zap, Bolt, GraduationCap } from "lucide-react";
import type { User as SupabaseUser } from '@supabase/supabase-js';

const Index = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { resolvedTheme } = useTheme();
  const { hasActiveSubscription } = useSubscription();
  const inputRef = useRef<HTMLInputElement>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  
  // User state (defined early for queue hook)
  const [user, setUser] = useState<SupabaseUser | null>(null);
  
  // Generation queue hook - now with direct generation support
  const { 
    queueItems, 
    generateDirect,
    cancelQueueItem, 
    getEstimatedWait,
    getItemsByType 
  } = useGenerationQueue(user?.id || null);

  // UI State
  const [activeTab, setActiveTab] = useState<'video' | 'image' | 'music' | 'speech' | 'chat'>('video');

  // Handle tab, model, and prompt from URL (e.g., from notification clicks or Explore page remix)
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    const modelParam = searchParams.get('model');
    const promptParam = searchParams.get('prompt');
    
    if (tabParam && ['video', 'image', 'music', 'speech', 'chat'].includes(tabParam)) {
      setActiveTab(tabParam as 'video' | 'image' | 'music' | 'speech' | 'chat');
      
      // Set the model if provided
      if (modelParam) {
        if (tabParam === 'video') {
          setVideoSettings(prev => ({ ...prev, model: modelParam }));
        } else if (tabParam === 'image') {
          setImageSettings(prev => ({ ...prev, model: modelParam }));
        } else if (tabParam === 'music') {
          setMusicSettings(prev => ({ ...prev, model: modelParam }));
        }
      }
      
      // Set the prompt if provided (remix feature)
      if (promptParam) {
        if (tabParam === 'video') {
          setPrompt(promptParam);
        } else if (tabParam === 'image') {
          setImagePrompt(promptParam);
        } else if (tabParam === 'music') {
          setMusicPrompt(promptParam);
        } else if (tabParam === 'speech') {
          setSpeechPrompt(promptParam);
        }
        toast({
          title: "Remix Ready",
          description: "Prompt and settings loaded. Hit generate!",
        });
      } else if (modelParam) {
        toast({
          title: "Model Selected",
          description: `Ready to create with ${modelParam}. Enter a prompt!`,
        });
      }
      
      // Clear params after processing
      searchParams.delete('tab');
      searchParams.delete('model');
      searchParams.delete('prompt');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);
  const [prompt, setPrompt] = useState("");
  const [imagePrompt, setImagePrompt] = useState("");
  const [musicPrompt, setMusicPrompt] = useState("");
  const [speechPrompt, setSpeechPrompt] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [isGeneratingImage, setIsGeneratingImage] = useState(false);
  const [isGeneratingMusic, setIsGeneratingMusic] = useState(false);
  const [isGeneratingSpeech, setIsGeneratingSpeech] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showCreditPurchase, setShowCreditPurchase] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [refreshGallery, setRefreshGallery] = useState(0);
  const [refreshImageGallery, setRefreshImageGallery] = useState(0);
  const [refreshMusicGallery, setRefreshMusicGallery] = useState(0);
  const [refreshSpeechGallery, setRefreshSpeechGallery] = useState(0);

  // Credits (user is defined earlier for queue hook)
  const [credits, setCredits] = useState<number | null>(null);

  // Video settings and cost
  const [videoSettings, setVideoSettings] = useState({
    model: "wan-super",
    duration: 5,
    aspect_ratio: "16:9",
    quality: "480p",
    motion_mode: "normal",
    style: "None",
    effect: "None",
    negative_prompt: "",
    start_image: "",
    last_frame_image: "",
    sound_effect_switch: false,
    sound_effect_content: "",
    enable_audio: false,
    motion_video: "",
    reference_images: [] as string[],
  });
  const [cost, setCost] = useState<number | null>(null);

  // Image settings and cost
  const [imageSettings, setImageSettings] = useState({
    model: "flux-schnell",
    aspect_ratio: "1:1",
    num_outputs: 1,
    output_format: "webp",
    source_images: [] as string[]
  });
  const [imageCost, setImageCost] = useState<number>(1); // Will be calculated dynamically

  // Music settings
  const [musicSettings, setMusicSettings] = useState<MusicSettings>({
    customMode: false,
    instrumental: false,
    model: "chirp-v5",
    title: "",
    tags: "",
    negativeTags: "",
    lyrics: ""
  });

  // Speech settings
  const [speechSettings, setSpeechSettings] = useState<SpeechSettings>({
    model: "kokoro-82m",
    language: "en",
    voice_id: "af_bella",
    emotion: "auto",
  });

  const defaultSpeechSettings: SpeechSettings = {
    model: "kokoro-82m",
    language: "en",
    voice_id: "af_bella",
    emotion: "auto",
  };

  const defaultMusicSettings: MusicSettings = {
    customMode: false,
    instrumental: false,
    model: "chirp-v5",
    title: "",
    tags: "",
    negativeTags: "",
    lyrics: ""
  };

  const defaultVideoSettings = {
    model: "wan-super",
    duration: 5,
    aspect_ratio: "16:9",
    quality: "480p",
    motion_mode: "normal",
    style: "None",
    effect: "None",
    negative_prompt: "",
    start_image: "",
    last_frame_image: "",
    sound_effect_switch: false,
    sound_effect_content: "",
    enable_audio: false,
    motion_video: "",
    quality_mode: "standard" as 'standard' | 'high',
    reference_images: [] as string[],
  };

  const defaultImageSettings = {
    model: "flux-schnell",
    aspect_ratio: "1:1",
    num_outputs: 1,
    output_format: "webp",
    source_images: [] as string[]
  };

  const hasChangedVideoSettings = () => {
    return JSON.stringify(videoSettings) !== JSON.stringify(defaultVideoSettings);
  };

  const hasChangedImageSettings = () => {
    return JSON.stringify(imageSettings) !== JSON.stringify(defaultImageSettings);
  };

  const hasChangedMusicSettings = () => {
    return JSON.stringify(musicSettings) !== JSON.stringify(defaultMusicSettings);
  };

  const hasChangedSpeechSettings = () => {
    return JSON.stringify(speechSettings) !== JSON.stringify(defaultSpeechSettings);
  };

  const handlePromptChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (activeTab === 'video') {
      setPrompt(e.target.value);
    } else if (activeTab === 'image') {
      setImagePrompt(e.target.value);
    } else if (activeTab === 'speech') {
      setSpeechPrompt(e.target.value);
    } else {
      setMusicPrompt(e.target.value);
    }
  };

  useEffect(() => {
    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setUser(session?.user ?? null);
        if (session?.user) {
          fetchCredits();
        } else {
          setCredits(null);
        }
      }
    );

    // Check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchCredits();
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  // Listen for use-prompt events from gallery clicks
  useEffect(() => {
    const handleUsePrompt = (event: CustomEvent<{ prompt: string; tab?: string }>) => {
      const { prompt: newPrompt, tab } = event.detail;
      
      // Switch to the correct tab and set the prompt
      if (tab === 'video' || !tab) {
        setActiveTab('video');
        setPrompt(newPrompt);
      } else if (tab === 'image') {
        setActiveTab('image');
        setImagePrompt(newPrompt);
      } else if (tab === 'music') {
        setActiveTab('music');
        setMusicPrompt(newPrompt);
      } else if (tab === 'speech') {
        setActiveTab('speech');
        setSpeechPrompt(newPrompt);
      }
      
      // Scroll to top and focus input
      window.scrollTo({ top: 0, behavior: 'smooth' });
      setTimeout(() => inputRef.current?.focus(), 300);
    };

    window.addEventListener('use-prompt', handleUsePrompt as EventListener);
    return () => window.removeEventListener('use-prompt', handleUsePrompt as EventListener);
  }, []);

  const fetchCredits = async () => {
    try {
      const { data, error } = await supabase.functions.invoke("check-credits");
      if (error) throw error;
      console.log("Fetched credits:", data.balance);
      setCredits(data.balance);
    } catch (error) {
      console.error("Error fetching credits:", error);
      setCredits(0);
    }
  };

  // Calculate video cost when settings change
  useEffect(() => {
    const calculateCost = async () => {
      if (!user) return;
      try {
        const { data, error } = await supabase.functions.invoke("calculate-video-cost", {
          body: { settings: videoSettings },
        });
        if (error) throw error;
        console.log("Calculated video cost:", data.cost);
        setCost(data.cost);
      } catch (error) {
        console.error("Error calculating video cost:", error);
        setCost(null);
      }
    };

    calculateCost();
  }, [videoSettings, user]);

  // Calculate image cost when settings change
  useEffect(() => {
    const calculateImageCost = async () => {
      if (!user) return;
      try {
        const { data, error } = await supabase.functions.invoke("calculate-image-cost", {
          body: { settings: imageSettings },
        });
        if (error) throw error;
        console.log("Calculated image cost:", data.cost);
        setImageCost(data.cost);
      } catch (error) {
        console.error("Error calculating image cost:", error);
        setImageCost(1); // Fallback to 1 credit
      }
    };

    calculateImageCost();
  }, [imageSettings, user]);

  const handleGenerate = async () => {
    if (activeTab === 'video') {
      await handleVideoGenerate();
    } else if (activeTab === 'image') {
      await handleImageGenerate();
    } else if (activeTab === 'speech') {
      await handleSpeechGenerate();
    } else {
      await handleMusicGenerate();
    }
  };

  const handleSpeechGenerate = async () => {
    if (!user) {
      setShowAuthModal(true);
      return;
    }

    if (!speechPrompt.trim()) {
      toast({
        title: t("main.enterPrompt"),
        description: "Please enter text to convert to speech",
        variant: "destructive",
      });
      return;
    }

    setIsGeneratingSpeech(true);

    try {
      const result = await generateDirect('speech', speechPrompt.trim(), { ...speechSettings });

      if (!result.success) {
        if (result.error?.includes('Insufficient credits') || result.error?.includes('credits')) {
          toast({
            title: t("main.insufficientCredits", "Insufficient credits"),
            description: result.required && result.available 
              ? t("main.insufficientCreditsDetails", `You need ${result.required} credits but only have ${result.available}`)
              : t("main.purchaseMoreCredits", "Purchase more credits to continue"),
            variant: "destructive",
          });
          setShowCreditPurchase(true);
        } else {
          toast({
            title: t("speech.generationError", "Generation Error"),
            description: result.error || "Failed to generate speech",
            variant: "destructive",
          });
        }
        return;
      }

      await fetchCredits();
      setSpeechPrompt("");
      setRefreshSpeechGallery(prev => prev + 1);

      if (result.queued) {
        toast({
          title: t("queue.queued", "Queued"),
          description: `Position #${result.position} • ${getEstimatedWait(result.position || 1, 'speech')}`,
        });
      } else {
        toast({
          title: t("speech.generating", "Generating speech..."),
          description: "Your speech is being created",
        });
      }
    } catch (error: any) {
      console.error("Speech generation error:", error);
      toast({
        title: t("speech.generationError", "Generation Error"),
        description: "Failed to generate speech",
        variant: "destructive",
      });
    } finally {
      setIsGeneratingSpeech(false);
    }
  };

  const handleVideoGenerate = async () => {
    if (!user) {
      setShowAuthModal(true);
      return;
    }

    // Wan Animate and Kling Motion Control require character image AND motion video (no prompt needed)
    const isMotionControlModel = videoSettings.model === 'wan-2.2-animate' || videoSettings.model === 'kling-motion-control';
    
    if (isMotionControlModel) {
      if (!videoSettings.start_image) {
        toast({
          title: t("video.characterImageRequired", "Character image required"),
          description: t("video.uploadCharacterImage", "Please upload a character image to animate"),
          variant: "destructive",
        });
        return;
      }
      if (!videoSettings.motion_video) {
        toast({
          title: t("video.motionVideoRequired", "Motion video required"),
          description: t("video.uploadMotionVideo", "Please upload a motion reference video"),
          variant: "destructive",
        });
        return;
      }
    } else if (!prompt.trim()) {
      toast({
        title: t("main.enterPrompt"),
        description: t("main.enterPrompt"),
        variant: "destructive",
      });
      return;
    }

    setIsGenerating(true);

    try {
      // For motion control models, prompt can be empty (use placeholder for DB)
      const videoPrompt = isMotionControlModel ? (prompt.trim() || 'Motion control') : prompt.trim();
      const result = await generateDirect('video', videoPrompt, { ...videoSettings });

      if (!result.success) {
        if (result.error?.includes('Insufficient credits') || result.error?.includes('credits')) {
          toast({
            title: t("main.insufficientCredits", "Insufficient credits"),
            description: result.required && result.available 
              ? t("main.insufficientCreditsDetails", `You need ${result.required} credits but only have ${result.available}`)
              : t("main.purchaseMoreCredits", "Purchase more credits to continue"),
            variant: "destructive",
          });
          setShowCreditPurchase(true);
        } else {
          toast({
            title: t("main.generationError"),
            description: result.error || "Failed to generate video",
            variant: "destructive",
          });
        }
        return;
      }

      await fetchCredits();
      setPrompt("");
      setRefreshGallery(prev => prev + 1);
      
      if (result.queued) {
        toast({
          title: t("queue.queued", "Queued"),
          description: `Position #${result.position} • ${getEstimatedWait(result.position || 1, 'video')}`,
        });
      } else {
        toast({
          title: t("main.generatingVideo", "Generating video..."),
          description: "Your video is being created",
        });
      }
    } catch (error: any) {
      console.error("Generation error:", error);
      toast({
        title: t("main.generationError"),
        description: "Failed to generate video",
        variant: "destructive",
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const handleMakeVideo = (imageUrl: string, imagePrompt: string) => {
    // Switch to video tab
    setActiveTab('video');
    
    // Pre-fill the video prompt with the image prompt
    setPrompt(imagePrompt);
    
    // Update video settings to include the start image
    setVideoSettings(prev => ({
      ...prev,
      start_image: imageUrl
    }));
    
    // Show success toast
    toast({
      title: t("main.imageToVideoSuccess"),
      description: t("main.imageToVideoSuccess"),
    });
  };

  // Model click handlers for public galleries
  const handleVideoModelClick = (model: string) => {
    setActiveTab('video');
    setVideoSettings(prev => ({ ...prev, model }));
    toast({
      title: "Model Selected",
      description: `Switched to ${model} model. Enter a prompt to generate!`,
    });
    // Scroll to top and focus input
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleImageModelClick = (model: string) => {
    setActiveTab('image');
    setImageSettings(prev => ({ ...prev, model }));
    toast({
      title: "Model Selected",
      description: `Switched to ${model} model. Enter a prompt to generate!`,
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleMusicModelClick = (model: string) => {
    setActiveTab('music');
    setMusicSettings(prev => ({ ...prev, model }));
    toast({
      title: "Model Selected",
      description: `Switched to ${model} model. Enter a prompt to generate!`,
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleImageGenerate = async () => {
    if (!user) {
      setShowAuthModal(true);
      return;
    }

    if (!imagePrompt.trim()) {
      toast({
        title: t("main.enterPrompt"),
        description: t("main.enterPrompt"),
        variant: "destructive",
      });
      return;
    }

    // Check if using a free model - requires subscription
    const isFreeModel = ['flux-schnell', 'hidream-l1-fast'].includes(imageSettings.model);
    if (isFreeModel && !hasActiveSubscription) {
      toast({
        title: t("subscription.required", "Subscription Required"),
        description: t("subscription.requiredDesc", "Subscribe to unlock free AI generation features"),
        variant: "destructive",
      });
      setShowSubscriptionModal(true);
      return;
    }

    const totalImageCost = imageCost;
    console.log("Image generation check:", { credits, imageCost: totalImageCost, user: !!user });
    
    if (credits === null || credits < totalImageCost) {
      console.log("Showing credit purchase modal - Image:", { credits, imageCost: totalImageCost, hasUser: !!user });
      setShowCreditPurchase(true);
      return;
    }

    setIsGeneratingImage(true);

    try {
      const result = await generateDirect('image', imagePrompt.trim(), { ...imageSettings });

      if (!result.success) {
        if (result.error?.includes('Insufficient credits') || result.error?.includes('credits')) {
          toast({
            title: t("main.insufficientCredits", "Insufficient credits"),
            description: result.required && result.available 
              ? t("main.insufficientCreditsDetails", `You need ${result.required} credits but only have ${result.available}`)
              : t("main.purchaseMoreCredits", "Purchase more credits to continue"),
            variant: "destructive",
          });
          setShowCreditPurchase(true);
        } else {
          toast({
            title: "Generation Error", 
            description: result.error || "Failed to generate image",
            variant: "destructive",
          });
        }
        return;
      }

      await fetchCredits();
      setImagePrompt("");
      setRefreshImageGallery(prev => prev + 1);
      
      if (result.queued) {
        toast({
          title: t("queue.queued", "Queued"),
          description: `Position #${result.position} • ${getEstimatedWait(result.position || 1, 'image')}`,
        });
      } else {
        toast({
          title: "Generating image...",
          description: "Your image is being created",
        });
      }
    } catch (error: any) {
      console.error("Image generation error:", error);
      toast({
        title: "Generation Error", 
        description: "Failed to generate image",
        variant: "destructive",
      });
    } finally {
      setIsGeneratingImage(false);
    }
  };

  // Suno prompt length limits
  const SUNO_SIMPLE_MODE_LIMIT = 500;
  const SUNO_CUSTOM_MODE_LIMIT = 3000;
  
  const getMusicPromptLimit = () => {
    return musicSettings.customMode ? SUNO_CUSTOM_MODE_LIMIT : SUNO_SIMPLE_MODE_LIMIT;
  };

  const handleMusicGenerate = async () => {
    if (!user) {
      setShowAuthModal(true);
      return;
    }

    if (!musicPrompt.trim()) {
      toast({
        title: t("main.enterPrompt"),
        description: "Please enter a music description",
        variant: "destructive",
      });
      return;
    }

    // Validate prompt length for Suno API
    const promptLimit = getMusicPromptLimit();
    if (musicPrompt.trim().length > promptLimit) {
      toast({
        title: "Prompt too long",
        description: musicSettings.customMode 
          ? `Custom mode prompts are limited to ${promptLimit} characters. You have ${musicPrompt.trim().length}.`
          : `Simple mode prompts are limited to ${promptLimit} characters. Switch to Custom Mode for longer prompts, or shorten your description.`,
        variant: "destructive",
      });
      return;
    }

    setIsGeneratingMusic(true);

    try {
      const result = await generateDirect('music', musicPrompt.trim(), { ...musicSettings });

      if (!result.success) {
        if (result.error?.includes('Insufficient credits') || result.error?.includes('credits')) {
          toast({
            title: t("main.insufficientCredits", "Insufficient credits"),
            description: result.required && result.available 
              ? t("main.insufficientCreditsDetails", `You need ${result.required} credits but only have ${result.available}`)
              : t("main.purchaseMoreCredits", "Purchase more credits to continue"),
            variant: "destructive",
          });
          setShowCreditPurchase(true);
        } else {
          toast({
            title: "Generation Error",
            description: result.error || "Failed to generate music",
            variant: "destructive",
          });
        }
        return;
      }

      await fetchCredits();
      setMusicPrompt("");
      setMusicSettings(defaultMusicSettings);
      setRefreshMusicGallery(prev => prev + 1);

      if (result.queued) {
        toast({
          title: t("queue.queued", "Queued"),
          description: `Position #${result.position} • ${getEstimatedWait(result.position || 1, 'music')}`,
        });
      } else {
        toast({
          title: "Generating music...",
          description: "Your music is being created",
        });
      }
    } catch (error: any) {
      console.error("Music generation error:", error);
      toast({
        title: "Generation Error",
        description: "Failed to generate music",
        variant: "destructive",
      });
    } finally {
      setIsGeneratingMusic(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="p-6 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <img 
            src={resolvedTheme === 'dark' ? "/lovable-uploads/bfe19e13-6d25-45d8-ab1b-fc3f36beedfa.png" : "/lovable-uploads/adc4a6b9-7b9f-47ab-ab9e-b3426809dcbe.png"} 
            alt="VideoPoet" 
            className="h-8" 
          />
          <Link to="/tutorials" className="hidden md:block">
            <Button 
              variant="outline" 
              size="sm"
              className="gap-2 bg-emerald-500/10 border-emerald-500/30 text-emerald-600 hover:bg-emerald-500/20 hover:text-emerald-700"
            >
              <GraduationCap className="h-4 w-4" />
              {t("tutorials.button", "Tutorials")}
            </Button>
          </Link>
        </div>
        <div className="flex items-center gap-2 md:gap-4">
          <LanguageSelector />
          {user && <NotificationBell />}
          {user && <UserCreditsStatus />}
          <HamburgerMenu />
        </div>
      </div>

      {/* Onboarding notifications for new users */}
      {user && <OnboardingNotification />}

      {/* Main Content */}
      <main className="flex flex-col items-center justify-center px-4">
        {/* Header for non-signed in users */}
        {!user && (
          <div className="text-center space-y-4 mb-8">
            <h2 className="text-4xl font-light text-foreground flex items-center justify-center gap-3">
              {t("main.heroTitle")}
              <Zap className="h-8 w-8 text-primary animate-pulse" />
            </h2>
            <p className="text-lg text-muted-foreground">
              {t("main.heroSubtitle")}
            </p>
          </div>
        )}

        {/* Tab Navigation */}
        <TabNavigation activeTab={activeTab} onTabChange={setActiveTab} />

        {/* Search-like Interface - hide for chat tab */}
        {activeTab !== 'chat' && (
          <div className="w-full max-w-2xl space-y-8">

            {/* Prompt input - hide for motion control models since they're image+video based */}
            {!(activeTab === 'video' && (videoSettings.model === 'wan-2.2-animate' || videoSettings.model === 'kling-motion-control')) && (
              <div className="flex flex-col md:flex-row gap-3 md:gap-4 items-stretch md:items-center">
              <div className="flex-1 relative">
                <Input
                  ref={inputRef}
                  value={activeTab === 'video' ? prompt : activeTab === 'image' ? imagePrompt : activeTab === 'speech' ? speechPrompt : musicPrompt}
                  onChange={handlePromptChange}
                  placeholder={user ? (
                    activeTab === 'video' ? t("main.promptPlaceholder") : 
                    activeTab === 'image' ? "Describe the image you want to generate..." :
                    activeTab === 'speech' ? t("speech.promptPlaceholder", "Enter text to convert to speech...") :
                    "Describe the music you want to create..."
                  ) : t("main.signInRequired")}
                  className="w-full text-sm md:text-lg py-2 md:py-3 px-4 md:px-6 h-10 md:h-12 rounded-full border-2 border-border focus:border-primary"
                  onKeyPress={(e) => e.key === "Enter" && handleGenerate()}
                />
                {/* Character counter for music tab */}
                {activeTab === 'music' && musicPrompt.length > 0 && (
                  <div className={`absolute -bottom-5 right-4 text-xs ${
                    musicPrompt.length > getMusicPromptLimit() 
                      ? 'text-destructive font-medium' 
                      : musicPrompt.length > getMusicPromptLimit() * 0.8 
                        ? 'text-amber-500' 
                        : 'text-muted-foreground'
                  }`}>
                    {musicPrompt.length}/{getMusicPromptLimit()}
                    {!musicSettings.customMode && musicPrompt.length > SUNO_SIMPLE_MODE_LIMIT * 0.8 && (
                      <span className="ml-1 text-muted-foreground">(Custom Mode: up to 3000)</span>
                    )}
                  </div>
                )}
              </div>
                <Button
                  onClick={handleGenerate}
                  variant="premium"
                  className="rounded-full px-6 md:px-8 py-2 md:py-3 h-10 md:h-12 flex flex-col justify-center items-center gap-0 w-full md:w-auto"
                  size="lg"
                  disabled={activeTab === 'video' ? isGenerating : activeTab === 'image' ? isGeneratingImage : activeTab === 'speech' ? isGeneratingSpeech : isGeneratingMusic}
                >
                  <span className="flex items-center gap-2 text-sm md:text-base">
                    {activeTab === 'video' ? t("main.generateButton") : 
                     activeTab === 'image' ? "Generate Image" : 
                     activeTab === 'speech' ? t("speech.generateButton", "Generate Speech") :
                     "Generate Music"}
                    {activeTab === 'image' && ['flux-schnell', 'hidream-l1-fast'].includes(imageSettings.model) && (
                      <span className="text-emerald-400 font-semibold">Free</span>
                    )}
                  </span>
                  {user && (
                    <span className="text-[10px] md:text-xs opacity-70">
                      {activeTab === 'video' && cost && t("main.costPreview", { cost })}
                      {activeTab === 'image' && (
                        ['flux-schnell', 'hidream-l1-fast'].includes(imageSettings.model)
                          ? (hasActiveSubscription ? "Included with subscription" : "Requires subscription")
                          : `${imageCost} credit${imageCost > 1 ? 's' : ''}`
                      )}
                      {activeTab === 'speech' && (
                        speechSettings.model === 'kokoro-82m' 
                          ? "Fixed 100 Cr"
                          : `~${Math.max(150, Math.ceil(speechPrompt.trim().length * 0.5)).toLocaleString()} cr`
                      )}
                      {activeTab === 'music' && (musicSettings.model === 'minimax-1.5' ? "450 credits" : "1,563 credits")}
                    </span>
                  )}
                </Button>
              </div>
            )}

            {/* Motion control models (Wan Animate / Kling Motion Control) - No prompt, just generate button */}
            {activeTab === 'video' && (videoSettings.model === 'wan-2.2-animate' || videoSettings.model === 'kling-motion-control') && (
              <div className="flex flex-col gap-3 items-center">
                <p className="text-muted-foreground text-sm text-center">
                  {videoSettings.model === 'kling-motion-control' 
                    ? "Upload a character image and motion video. Audio from the motion video will be preserved."
                    : "Upload a start image to animate it. Optionally add a motion reference video."}
                </p>
                <Button
                  onClick={handleGenerate}
                  variant="premium"
                  className="rounded-full px-6 md:px-8 py-2 md:py-3 h-10 md:h-12 flex flex-col justify-center items-center gap-0"
                  size="lg"
                  disabled={isGenerating || !videoSettings.start_image || (videoSettings.model === 'kling-motion-control' && !videoSettings.motion_video)}
                >
                  <span className="flex items-center gap-2 text-sm md:text-base">
                    {t("main.generateButton")}
                  </span>
                  {user && cost && (
                    <span className="text-[10px] md:text-xs opacity-70">
                      {t("main.costPreview", { cost })}
                    </span>
                  )}
                </Button>
                {!videoSettings.start_image && (
                  <p className="text-destructive text-xs">
                    {videoSettings.model === 'kling-motion-control' ? "Character image required" : "Start image required for animation"}
                  </p>
                )}
                {videoSettings.model === 'kling-motion-control' && videoSettings.start_image && !videoSettings.motion_video && (
                  <p className="text-destructive text-xs">Motion video required</p>
                )}
              </div>
            )}

            {/* Queue Status - show active queue items for current tab */}
            {user && queueItems.length > 0 && (
              <QueueStatus
                items={getItemsByType(activeTab as 'video' | 'image' | 'music' | 'speech')}
                onCancel={cancelQueueItem}
                getEstimatedWait={getEstimatedWait}
              />
            )}

            {user && activeTab === 'speech' && (
              <SpeechQuickSettings 
                settings={speechSettings}
                onSettingsChange={setSpeechSettings}
                hasChangedSettings={hasChangedSpeechSettings()}
                onUseDefaults={() => {
                  setSpeechSettings(defaultSpeechSettings);
                  toast({
                    title: "Reset to Defaults",
                    description: "Speech settings reset to defaults"
                  });
                }}
                textLength={speechPrompt.trim().length}
              />
            )}

            {user && activeTab === 'video' && (
              <VideoQuickSettings 
                settings={videoSettings}
                onSettingsChange={setVideoSettings}
                hasChangedSettings={hasChangedVideoSettings()}
                onUseDefaults={() => {
                  setVideoSettings(defaultVideoSettings);
                  toast({
                    title: t("advancedSettings.resetToDefaults"),
                    description: t("advancedSettings.resetToDefaults")
                  });
                }}
              />
            )}
            
            {user && activeTab === 'image' && (
              <ImageQuickSettings 
                settings={imageSettings}
                onSettingsChange={setImageSettings}
                hasChangedSettings={hasChangedImageSettings()}
                onUseDefaults={() => {
                  setImageSettings(defaultImageSettings);
                  toast({
                    title: "Reset to Defaults",
                    description: "Image settings reset to defaults"
                  });
                }}
              />
            )}
            
            {user && activeTab === 'music' && (
              <MusicQuickSettings 
                settings={musicSettings}
                onSettingsChange={setMusicSettings}
                hasChangedSettings={hasChangedMusicSettings()}
                onUseDefaults={() => {
                  setMusicSettings(defaultMusicSettings);
                  toast({
                    title: "Reset to Defaults",
                    description: "Music settings reset to defaults"
                  });
                }}
              />
            )}

          </div>
        )}

        {/* Chat Interface */}
        {activeTab === 'chat' && (
          <div className="w-full max-w-2xl">
            <ChatInterface />
          </div>
        )}

        {/* Gallery - hide for chat tab */}
        {activeTab !== 'chat' && (
          <div className="w-full max-w-6xl mt-16">
            <h2 className="text-2xl font-semibold mb-6 text-center">
              {activeTab === 'video' 
                ? (user ? t("gallery.title") : "Public Generated Videos")
                : activeTab === 'image'
                  ? (user ? "Your Generated Images" : "Public Generated Images")
                  : activeTab === 'speech'
                    ? (user ? "Your Generated Speech" : "AI Generated Speech")
                    : (user ? "Your Generated Music" : "Public Generated Music")
              }
            </h2>
            {activeTab === 'video' && (
              <VideoGallery 
                refreshTrigger={refreshGallery} 
                onModelClick={!user ? handleVideoModelClick : undefined}
                onRetry={user ? (prompt, settings) => {
                  setPrompt(prompt);
                  if (settings?.model) setVideoSettings(prev => ({ ...prev, model: settings.model }));
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                  toast({ title: "Ready to retry", description: "Hit generate to try again" });
                } : undefined}
              />
            )}
            {activeTab === 'image' && (
              <ImageGallery 
                user={user} 
                refreshTrigger={refreshImageGallery} 
                onMakeVideo={user ? handleMakeVideo : undefined}
                onModelClick={!user ? handleImageModelClick : undefined}
                onRetry={user ? (prompt, settings) => {
                  setImagePrompt(prompt);
                  if (settings?.model) setImageSettings(prev => ({ ...prev, model: settings.model }));
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                  toast({ title: "Ready to retry", description: "Hit generate to try again" });
                } : undefined}
              />
            )}
            {activeTab === 'speech' && (
              <SpeechGallery 
                user={user} 
                refreshTrigger={refreshSpeechGallery}
              />
            )}
            {activeTab === 'music' && (
              <MusicGallery 
                user={user} 
                refreshTrigger={refreshMusicGallery}
                onModelClick={!user ? handleMusicModelClick : undefined}
                onRetry={user ? (prompt, settings) => {
                  setMusicPrompt(prompt);
                  if (settings?.model) setMusicSettings(prev => ({ ...prev, model: settings.model }));
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                  toast({ title: "Ready to retry", description: "Hit generate to try again" });
                } : undefined}
              />
            )}
          </div>
        )}

        {/* Auth Modal */}
        <AuthModal 
          isOpen={showAuthModal}
          onClose={() => setShowAuthModal(false)}
          onAuthSuccess={() => {
            setShowAuthModal(false);
            fetchCredits();
          }}
        />

        {/* Credit Purchase Modal */}
        <CreditPurchaseModal 
          open={showCreditPurchase}
          onOpenChange={setShowCreditPurchase}
          onPurchaseComplete={() => {
            setShowCreditPurchase(false);
            fetchCredits();
          }}
        />

        {/* Subscription Modal */}
        <SubscriptionModal 
          open={showSubscriptionModal}
          onOpenChange={setShowSubscriptionModal}
        />

        {/* Privacy Banner */}
        <PrivacyBanner />
      </main>
    </div>
  );
};

export default Index;